import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useChatContext } from "@/contexts/ChatContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Building2,
  Search,
  Send,
  ShieldCheck,
  KeyRound,
  Loader2,
  Info,
  CheckCircle2,
  Clock,
  MessageSquare,
  Users,
  ChevronDown,
  ChevronRight,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useUnreadJoinRequestResponses } from "@/hooks/useUnreadJoinRequestResponses";
import { useBrokerMemberships, type BrokerMembership } from "@/hooks/useBrokerMemberships";
import { LeaveNetworkDialog } from "@/components/lr-direct/LeaveNetworkDialog";

interface ShipperResult {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  documents_verified_at: string | null;
  lr_direct_accept_requests: boolean;
  is_member: boolean;
  has_pending_request: boolean;
}

function normalizeId(raw: string): string {
  return raw.toUpperCase().replace(/\s+/g, "");
}

export default function LRDirectBrokerMyNetworkPage() {
  usePageMeta({
    title: "LR Direct — My Network | LaneRunner",
    description: "Manage the shipper networks you're connected to on LR Direct.",
  });

  const { user, profile } = useAuth();
  const { openChatWithUser } = useChatContext();
  const navigate = useNavigate();
  const responseCount = useUnreadJoinRequestResponses(user?.id ?? null);

  // Network access is role-neutral across LR Direct providers (brokers and
  // carriers). Billing tier still gates bidding, not network membership.
  const isProvider =
    profile?.user_type === "broker" ||
    profile?.user_type === "broker_carrier" ||
    profile?.user_type === "carrier";

  const { data: memberships = [], isLoading, refetch } = useBrokerMemberships(
    isProvider ? user?.id ?? null : null,
  );

  // Find-shipper (previously the whole /lr-direct/shippers page) expanded by
  // default — make the lookup visible immediately on page load.
  const [findOpen, setFindOpen] = useState(true);
  const [idInput, setIdInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<ShipperResult | null | "not_found">(null);
  const [openRequest, setOpenRequest] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSearch = async () => {
    const raw = idInput.trim();
    if (raw.length < 4) {
      toast.error("Enter the full Private Network ID");
      return;
    }
    setSearching(true);
    setResult(null);
    try {
      const { data, error } = await supabase.rpc(
        "find_shipper_by_lr_direct_id" as any,
        { _id: raw },
      );
      if (error) throw error;
      const row = Array.isArray(data) ? (data[0] as any) : null;
      setResult(row ? (row as ShipperResult) : "not_found");
    } catch (e: any) {
      toast.error(e?.message || "Lookup failed");
      setResult(null);
    } finally {
      setSearching(false);
    }
  };

  const submitRequest = async () => {
    if (!result || result === "not_found") return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("request-network-membership", {
        body: { shipperCompanyId: result.id, message: message.trim() || undefined },
      });
      const bodyError = (data as any)?.error as string | undefined;
      if (bodyError) {
        const lower = bodyError.toLowerCase();
        if (lower.includes("already in this shipper")) {
          toast.success("You're already in this network");
          setOpenRequest(false);
          setResult({ ...result, is_member: true });
          refetch();
          return;
        }
        if (lower.includes("already have a pending request")) {
          toast.message("Request already pending", {
            description: "The shipper hasn't responded yet.",
          });
          setOpenRequest(false);
          setResult({ ...result, has_pending_request: true });
          return;
        }
        throw new Error(bodyError);
      }
      if (error) throw error;
      toast.success(`Request sent to ${result.name}`);
      setOpenRequest(false);
      setMessage("");
      setResult({ ...result, has_pending_request: true });
    } catch (e: any) {
      toast.error(e?.message || "Failed to send request");
    } finally {
      setSending(false);
    }
  };

  const handleMessage = (m: BrokerMembership) => {
    openChatWithUser(m.shipper_user_id, m.shipper_name);
  };

  if (!isProvider) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        My Network is available to LR Direct member accounts.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 shrink-0"
              onClick={() => navigate("/lr-direct")}
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <div className="h-6 w-px bg-border hidden sm:block" />
            <div className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg bg-brand-teal/10 shrink-0">
              <Users className="h-5 w-5 text-brand-teal" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-foreground truncate">My Network</h1>
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 sm:truncate">
                Shippers you're connected to on LR Direct.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/lr-direct/network-invitations")}
            className="relative gap-2 h-10 sm:h-8 w-full sm:w-auto"
          >
            <Inbox className="h-4 w-4" />
            Invitations & Requests
            {responseCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold">
                {responseCount > 9 ? "9+" : responseCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-5 sm:px-6 sm:py-6 space-y-5">

        {/* Find a shipper — collapsible (top) */}
        <Card className="overflow-hidden">
          <button
            type="button"
            onClick={() => setFindOpen((v) => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/40 transition"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-teal/10">
                <KeyRound className="h-4 w-4 text-brand-teal" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-sm">Find a shipper</div>
                <div className="text-xs text-muted-foreground">
                  Look up an LR Direct shipper by their Private Network ID.
                </div>
              </div>
            </div>
            {findOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>

          {findOpen && (
            <div className="border-t border-border p-4 space-y-4">
              <div className="rounded-md bg-muted/30 border border-dashed border-border p-3 flex gap-2">
                <Info className="h-4 w-4 text-brand-teal shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground">Private by design.</span>{" "}
                  Shippers aren't listed publicly. Ask them for their 8-character Private Network
                  ID (e.g.{" "}
                  <code className="px-1 py-0.5 rounded bg-muted font-mono text-[11px]">
                    AB7K9X2M
                  </code>
                  ).
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  Private Network ID
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="e.g. AB7K9X2M"
                      value={idInput}
                      onChange={(e) => setIdInput(normalizeId(e.target.value))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                      className="pl-8 h-11 sm:h-10 font-mono tracking-widest uppercase"
                      maxLength={12}
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={searching || idInput.trim().length < 4}
                    className="gap-2 text-white h-11 sm:h-10 px-5 w-full sm:w-auto shrink-0"
                    style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                  >

                    {searching ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    {searching ? "Searching…" : "Look up"}
                  </Button>
                </div>
              </div>

              {result === "not_found" && (
                <div className="rounded-md border border-border bg-muted/20 p-4 text-center">
                  <Building2 className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm font-semibold mb-1">No shipper found with that ID</p>
                  <p className="text-xs text-muted-foreground">
                    Double-check the ID. IDs are 8 characters, letters and numbers only.
                  </p>
                </div>
              )}

              {result && result !== "not_found" && (
                <div className="rounded-md border border-border p-4 space-y-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted shrink-0">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-sm flex items-center gap-1.5 flex-wrap">
                        {result.name}
                        {result.documents_verified_at && (
                          <ShieldCheck className="h-4 w-4 text-brand-teal shrink-0" />
                        )}
                        {result.is_member && (
                          <Badge className="bg-brand-teal/15 text-brand-teal border-0 text-[10px] uppercase font-bold gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Already a member
                          </Badge>
                        )}
                        {!result.is_member && result.has_pending_request && (
                          <Badge className="bg-amber-500/15 text-amber-600 border-0 text-[10px] uppercase font-bold gap-1">
                            <Clock className="h-3 w-3" /> Request pending
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {[result.city, result.state].filter(Boolean).join(", ") ||
                          "Location not set"}
                      </div>
                    </div>
                  </div>

                  {result.is_member ? (
                    <Button disabled variant="outline" className="w-full gap-2">
                      <CheckCircle2 className="h-4 w-4" /> You're already in this network
                    </Button>
                  ) : result.has_pending_request ? (
                    <Button disabled variant="outline" className="w-full gap-2">
                      <Clock className="h-4 w-4" /> Request pending review
                    </Button>
                  ) : result.lr_direct_accept_requests ? (
                    <Button
                      onClick={() => {
                        setMessage("");
                        setOpenRequest(true);
                      }}
                      className="w-full gap-2 text-white"
                      style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                    >
                      <Send className="h-4 w-4" /> Request to join network
                    </Button>
                  ) : (
                    <Button disabled variant="outline" className="w-full">
                      Not accepting requests
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Networks I'm in */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Networks You're In
              {memberships.length > 0 && (
                <span className="ml-2 text-foreground">({memberships.length})</span>
              )}
            </h2>
          </div>

          {isLoading ? (
            <Card className="p-8 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading your networks…
            </Card>
          ) : memberships.length === 0 ? (
            <Card className="p-8 text-center">
              <Users className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm font-semibold mb-1">You're not in any shipper networks yet</p>
              <p className="text-xs text-muted-foreground mb-4">
                Ask a shipper for their 8-character Private Network ID, then request to join above.
              </p>
              <Button
                onClick={() => setFindOpen(true)}
                className="gap-2 text-white"
                style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              >
                <Search className="h-4 w-4" /> Find a shipper
              </Button>
            </Card>
          ) : (
            <div className="space-y-2.5">
              {memberships.map((m) => (
                <Card key={m.network_id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-[200px] flex-1 basis-[220px]">

                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted shrink-0">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                          <span className="break-words">{m.shipper_name}</span>
                          {m.shipper_documents_verified_at && (
                            <ShieldCheck className="h-4 w-4 text-brand-teal shrink-0" />
                          )}
                          {m.active_dispatch_count > 0 && (
                            <Badge className="bg-amber-500/15 text-amber-600 border-0 text-[10px] uppercase font-bold gap-1 whitespace-nowrap">
                              <Clock className="h-3 w-3" />
                              {m.active_dispatch_count} active
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 break-words leading-relaxed">
                          {m.shipper_network_id && (
                            <>
                              <span className="font-mono">{m.shipper_network_id.toUpperCase()}</span>
                              {" · "}
                            </>
                          )}
                          {m.network_name || "Private network"}
                          {m.joined_at && (
                            <>
                              {" · Joined "}
                              {new Date(m.joined_at).toLocaleDateString()}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 w-full basis-full min-[520px]:w-auto min-[520px]:basis-auto min-[520px]:flex-nowrap [&_button]:h-10 [&_button]:flex-1 min-[520px]:[&_button]:h-8 min-[520px]:[&_button]:flex-none [&>span]:flex-1 [&>span]:flex [&>span>button]:w-full min-[520px]:[&>span]:flex-none">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => handleMessage(m)}
                      >
                        <MessageSquare className="h-3.5 w-3.5" /> Message
                      </Button>
                      <LeaveNetworkDialog
                        networkId={m.network_id}
                        shipperName={m.shipper_name}
                        activeDispatchCount={m.active_dispatch_count}
                        onLeft={() => refetch()}
                      />
                    </div>
                  </div>
                </Card>
              ))}

            </div>
          )}
        </div>
      </div>

      {/* Join-request dialog */}
      <Dialog open={openRequest} onOpenChange={(o) => !o && setOpenRequest(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Request to join {result && result !== "not_found" ? result.name : ""}
            </DialogTitle>
            <DialogDescription>
              Tell them why your operation is a good fit. They'll review your company before
              approving.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="We run the I-95 corridor weekly with 12 reefer trucks. Available for dedicated contracts."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={500}
            disabled={sending}
          />
          <p className="text-[11px] text-muted-foreground">
            {message.length}/500 characters · You can have up to 10 pending requests at a time.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenRequest(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              onClick={submitRequest}
              disabled={sending}
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              className="gap-2 text-white"
            >
              <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
